import { Center, Loader } from "@mantine/core"
import { TanStackDevtools } from "@tanstack/react-devtools"
import { createRootRouteWithContext, Outlet, useRouter } from "@tanstack/react-router"
import { getVersion } from "@tauri-apps/api/app"
import { type ErrorInfo, useCallback, useEffect, useRef, useState } from "react"
import { ErrorBoundary } from "../components/ErrorBoundary"
import { NotFound } from "../components/NotFound"
import { VersionBadge } from "../components/VersionBadge"
import { useAuth } from "../hooks/useAuth"
import { authClient } from "../lib/auth-client"
import { relayClientEvents, setReportingCompany, setReportingShellVersion } from "../lib/client-error-reporting"
import { applyCompanyDefaultLexTheme, coerceLexTheme } from "../lib/lex-themes"
import { IS_TAURI } from "../lib/runtime-origin"
import { applyBranding, detectProduct, fetchTenantBranding, TenantContext, type TenantState } from "../lib/tenant"
import type { RouterContext } from "../router"

function RootComponent() {
  const { user, loading: authLoading } = useAuth()
  const [tenantState, setTenantState] = useState<TenantState>({
    isAdmin: true,
    mcpUrl: null,
    product: detectProduct(),
    branding: null,
    loading: true,
  })
  const router = useRouter()

  // Build markers for the corner badge. The web sha is injected at build time
  // (VITE_COMMIT_SHA); the shell version is only available inside the Tauri
  // desktop app and may reject on older shells that haven't granted the
  // app-version permission yet — in which case we silently show the web sha alone.
  const webSha = (import.meta.env.VITE_COMMIT_SHA ?? "").slice(0, 7)
  const [shellVersion, setShellVersion] = useState<string | undefined>(undefined)
  useEffect(() => {
    if (!IS_TAURI) return
    let cancelled = false
    getVersion()
      .then((version) => {
        if (cancelled) return
        setShellVersion(version)
        setReportingShellVersion(version)
      })
      .catch(() => {
        // Pre-0.1.3 shell without the app-version capability — web sha only.
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Keep the error-reporting layer's active tenant in sync with the session, so
  // the out-of-React window handlers (and the boundary below) can scope the relay.
  useEffect(() => {
    setReportingCompany(user?.activeOrganizationId ?? null)
  }, [user?.activeOrganizationId])

  // Relay render-time crashes through the shared reporter, which tags the event
  // with surface + build versions and lands it on the tenant's diagnostics
  // channel. Falls back to console.error when no active company is resolved
  // (e.g. logged out / no org).
  const relayRenderError = useCallback((error: Error, errorInfo: ErrorInfo): void => {
    const relayed = relayClientEvents([
      {
        eventType: "client_render_error",
        severity: "error",
        message: error.message,
        context: {
          name: error.name,
          stack: error.stack,
          componentStack: errorInfo.componentStack,
          url: window.location.href,
        },
      },
    ])
    if (!relayed) {
      console.error("[ErrorBoundary] render error (no active company, not relayed)", error)
    }
  }, [])

  useEffect(() => {
    fetchTenantBranding().then(({ branding, mcpUrl }) => {
      const product = branding?.product ?? detectProduct()
      setTenantState({ isAdmin: !branding, product, branding, mcpUrl, loading: false })
      if (branding) {
        applyBranding(branding)
        // Theme the login page (and any not-yet-authed view) with the company
        // default — only when the user hasn't already chosen (preview/cached).
        if (product === "lexicanon" && branding.defaultTheme) {
          applyCompanyDefaultLexTheme(coerceLexTheme(branding.defaultTheme))
        }
      }
    })
  }, [])

  // Auto-activate organization when on a company subdomain
  const activatedRef = useRef(false)
  useEffect(() => {
    if (activatedRef.current) return
    if (!user || !tenantState.branding || user.activeOrganizationId) return

    // User is logged in on a company subdomain but no org is active — activate it
    const orgId = tenantState.branding.organizationId
    if (!orgId) return

    activatedRef.current = true
    authClient.organization
      .setActive({ organizationId: orgId })
      .then(() => {
        // Reload to pick up the new active org in the session
        window.location.reload()
      })
      .catch(() => {
        activatedRef.current = false
      })
  }, [user, tenantState.branding])

  // Keep router context in sync with auth state
  useEffect(() => {
    router.update({
      context: {
        ...router.options.context,
        auth: user,
        tenant: tenantState,
      },
    })
  }, [user, tenantState, router])

  if (authLoading || tenantState.loading) {
    // Dynamic viewport height (100dvh) so the loading splash fills the screen
    // under the iOS Safari URL bar instead of being clipped.
    return (
      <Center h="100dvh">
        <Loader size="lg" />
      </Center>
    )
  }

  return (
    <ErrorBoundary onError={relayRenderError} resetKeys={[router.state.location.pathname]}>
      <TenantContext.Provider value={tenantState}>
        <Outlet />
        {user?.isAdmin ? <VersionBadge web={webSha} shell={shellVersion} /> : null}
        {import.meta.env.DEV && <TanStackDevtools />}
      </TenantContext.Provider>
    </ErrorBoundary>
  )
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,
  notFoundComponent: NotFound,
})
